import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useUser } from './useUser';
import { useViewerItemStore, ItemType } from '../store/useViewerItemStore';

/**
 * Composable for managing viewer layout state and detection.
 * Determines which layout to show based on user role and item type.
 *
 * Layout routing:
 *   Video + Owner   → VideoOwnerLayout
 *   Video + Guest   → VideoGuestLayout
 *   Content + Owner → ContentOwnerLayout (image, PDF, audio, other)
 *   Content + Guest → ContentGuestLayout (image, PDF, audio, other)
 */
export const useViewerLayout = () => {
  const store = useViewerItemStore();
  const { type } = storeToRefs(store);
  const { isCurrentItemOwner } = useUser();

  const isVideo = computed(() => type.value === ItemType.VIDEO);

  // Owner layouts
  const shouldShowVideoOwnerLayout = computed(() => isVideo.value && isCurrentItemOwner.value);
  const shouldShowContentOwnerLayout = computed(() => !isVideo.value && isCurrentItemOwner.value);

  // Guest layouts
  const shouldShowVideoGuestLayout = computed(() => isVideo.value && !isCurrentItemOwner.value);
  const shouldShowContentGuestLayout = computed(() => !isVideo.value && !isCurrentItemOwner.value);

  return {
    isVideo,
    isCurrentItemOwner,

    // Layout routing
    shouldShowVideoOwnerLayout,
    shouldShowContentOwnerLayout,
    shouldShowVideoGuestLayout,
    shouldShowContentGuestLayout,
  };
};
